import { Button, Spinner } from "@fluentui/react-components";
import { CheckmarkCircle20Regular, DismissCircle20Regular, Search20Regular } from "@fluentui/react-icons";
import moment from "moment";
import { useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import * as ComponentFunctions from "./Debt.Details.Functions";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";
import { agingBucket, daysSince, initialsOf, isPending } from "../../General/Cashflow.Dashboard.Functions";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import { GuideDrawer, GuideDrawerSection } from "../../../../theme/components/GuideDrawer";
import "../../CashFlow.Detail.Styles.css";

const fmt = (amount: number | undefined | null, currency: string | undefined) =>
    formatCurrencyAmountWithIssue(amount ?? 0, currency || 'ARS');

const Party = ({ role, name }: { role: string; name: string }) => (
    <div className="cfd-party">
        <span className={`cfd-avatar${name === MEDICAL_CENTER_NAME ? ' cfd-avatar--me' : ''}`} aria-hidden>{initialsOf(name)}</span>
        <div className="cfd-party__meta">
            <span className="cfd-party__role">{role}</span>
            <span className="cfd-party__name" title={name}>{name}</span>
        </div>
    </div>
);

/**
 * Detalle de una deuda, en el orden en que se lee: cuánto falta, entre quiénes,
 * de dónde viene (liquidación, protocolo) y si el sistema la da por saldada.
 */
const DebtDetailsComponent = (props: ComponentFunctions.Props) => {

    useEffect(() => {
        if (props.isOpen) {
            if (props.DebtId && !props.CurrentDebt) {
                props.GetDebtById(props.DebtId);
            }
            props.GetAllPersons();
            props.GetAllMedicalProtocols();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.DebtId]);

    const debt = props.CurrentDebt;
    const protocol = props.CurrentProtocol;
    const isLoading = props.isLoading && !debt;

    const renderVerification = () => {
        if (!debt) return null;
        const checkingThis = props.isCheckingDebtStatus && props.checkedDebtStatusId === debt.id;
        const resultForThis = !props.isCheckingDebtStatus && props.successCheckingDebtStatus && props.checkedDebtStatusId === debt.id;

        if (checkingThis) return <span className="cfd-inline"><Spinner size="extra-tiny" /> <span className="cfd-hint">Verificando en el sistema…</span></span>;
        if (resultForThis) {
            return props.debtStatusResult
                ? <span className="cfd-tag cfd-tag--ok"><CheckmarkCircle20Regular /> Saldada en el sistema</span>
                : <span className="cfd-tag cfd-tag--bad"><DismissCircle20Regular /> Con saldo en el sistema</span>;
        }
        return (
            <Button appearance="secondary" icon={<Search20Regular />} onClick={() => props.CheckDebtStatus(debt.id)}>
                Verificar estado
            </Button>
        );
    };

    const renderBody = () => {
        if (isLoading) return <div className="cfd-state"><Spinner size="small" /> Cargando deuda…</div>;
        if (!debt) return <div className="cfd-state">No se encontró la deuda seleccionada.</div>;

        const currency = debt.currencyCode || 'ARS';
        const pending = isPending(debt);
        const original = debt.originalAmount || 0;
        const paid = debt.paidAmount || 0;
        const paidPct = original > 0 ? Math.min(100, (paid / original) * 100) : 0;
        const days = daysSince(debt.debtDate);
        const debtorName = props.Debtor?.businessName || debt.debtor;
        const creditorName = props.Creditor?.businessName || debt.creditor;
        const converted = !!debt.originalCurrencyCode && debt.originalCurrencyCode.toUpperCase() !== currency.toUpperCase();

        let statusTag = <span className="cfd-tag cfd-tag--settled"><CheckmarkCircle20Regular /> Saldada</span>;
        if (pending) {
            statusTag = paid > 0
                ? <span className="cfd-tag cfd-tag--partial">Parcialmente pagada</span>
                : <span className="cfd-tag cfd-tag--pending">Pendiente</span>;
        }

        return (
            <div className="cfd-root">
                {/* Cuánto falta */}
                <div className="cfd-hero">
                    <div className="cfd-hero__top">
                        <div>
                            <div className="cfd-hero__label">{pending ? 'Saldo pendiente' : 'Deuda saldada'}</div>
                            <div className={`cfd-hero__amount cfd-num ${pending ? 'cfd-hero__amount--pending' : 'cfd-hero__amount--ok'}`}>
                                {fmt(pending ? debt.pendingAmount : original, currency)}
                            </div>
                        </div>
                        <div className="cfd-hero__tags">
                            {statusTag}
                            {pending && <span className={`cfd-tag cfd-tag--${agingBucket(days)}`}>{days} días</span>}
                        </div>
                    </div>
                    <div className="cfd-bar" role="progressbar" aria-label="Pagado sobre el original" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(paidPct)}>
                        <span style={{ width: `${paidPct}%` }} />
                    </div>
                    <div className="cfd-figures">
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Original</div>
                            <div className="cfd-figure__value cfd-num">{fmt(original, currency)}</div>
                        </div>
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Pagado · {paidPct.toFixed(0)}%</div>
                            <div className="cfd-figure__value cfd-figure__value--ok cfd-num">{fmt(paid, currency)}</div>
                        </div>
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Pendiente</div>
                            <div className={`cfd-figure__value cfd-num ${pending ? 'cfd-figure__value--pending' : 'cfd-figure__value--muted'}`}>
                                {pending ? fmt(debt.pendingAmount, currency) : '—'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Entre quiénes */}
                <GuideDrawerSection title="Partes">
                    <div className="cfd-parties">
                        <Party role="Deudor" name={debtorName} />
                        <span className="cfd-party__arrow" aria-hidden>→</span>
                        <Party role="Acreedor" name={creditorName} />
                    </div>
                </GuideDrawerSection>

                {/* De dónde viene */}
                <GuideDrawerSection title="Datos de la deuda">
                    <dl className="cfd-rows">
                        <div className="cfd-row"><dt>Fecha</dt><dd className="cfd-num">{debt.debtDate ? moment(debt.debtDate).format('DD/MM/YYYY') : '—'}</dd></div>
                        <div className="cfd-row"><dt>Moneda</dt><dd>{currency.toUpperCase()}</dd></div>
                        {converted && (
                            <>
                                <div className="cfd-row"><dt>Importe en moneda origen</dt><dd className="cfd-num">{fmt(debt.originalAmountInOriginalCurrency, debt.originalCurrencyCode)}</dd></div>
                                <div className="cfd-row"><dt>Tipo de cambio</dt><dd className="cfd-num">{debt.currencyExchangeRate ? debt.currencyExchangeRate.toFixed(4) : '—'}</dd></div>
                            </>
                        )}
                        <div className="cfd-row">
                            <dt>Liquidación</dt>
                            <dd>{debt.settlementId
                                ? <Link className="cfd-link" to={`/settlements/${debt.settlementId}`}>LIQ-{debt.settlementId}</Link>
                                : <span className="cfd-empty">No viene de una liquidación</span>}</dd>
                        </div>
                        {debt.protocolId && (
                            <div className="cfd-row">
                                <dt>Protocolo</dt>
                                <dd>
                                    {protocol ? (
                                        <>
                                            <Link className="cfd-link" to={`/protocols/${protocol.id}`}>{protocol.protocolNumber || protocol.title || `#${protocol.id}`}</Link>
                                            {(protocol.title || protocol.laboratory?.title) && (
                                                <div className="cfd-hint">{[protocol.title, protocol.laboratory?.title].filter(Boolean).join(' · ')}</div>
                                            )}
                                        </>
                                    ) : <span className="cfd-plain">#{debt.protocolId}</span>}
                                </dd>
                            </div>
                        )}
                    </dl>
                </GuideDrawerSection>

                {/* Qué dice el sistema */}
                <GuideDrawerSection title="Estado en el sistema">
                    <div className="cfd-inline">{renderVerification()}</div>
                    {pending && (
                        <div className="cfd-hint">Faltan <strong>{fmt(debt.pendingAmount, currency)}</strong> para darla por saldada.</div>
                    )}
                </GuideDrawerSection>

                <GuideDrawerSection title="Comentarios">
                    {debt.comments && debt.comments.trim().length > 0
                        ? <div className="cfd-comment">{debt.comments}</div>
                        : <span className="cfd-empty">Sin comentarios.</span>}
                </GuideDrawerSection>

            </div>
        );
    };

    const eyebrow = ['Flujo de caja', debt ? `Deuda #${debt.id}` : undefined, debt?.settlementId ? `LIQ-${debt.settlementId}` : undefined]
        .filter(Boolean)
        .join(' · ');

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title="Detalle de deuda"
            eyebrow={eyebrow}
            onDismiss={props.onClose}
            closeLabel="Cerrar"
            testId="debt-details-drawer"
        >
            {renderBody()}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtDetailsComponent as any) as React.FC<{
    DebtId: number | undefined;
    isOpen: boolean;
    onClose: () => void;
}>;
